/** @format */

import React, { useState } from 'react';
import { Table, Button, Input, Space } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { Policy } from '../services/getPolicies';
import { text } from 'stream/consumers';

interface ACARequestsTableProps {
  data: Partial<Policy>[];
  loading: boolean;
  error?: any;
  onView?: (policyId: string) => void;
}

const ACARequestsTable: React.FC<ACARequestsTableProps> = ({ data, loading, error, onView }) => {
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const capitalizeFullName = (name?: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const dateFormat = (date?: string) => {
    if (!date) return '';
    // Parse as YYYY-MM-DD string to avoid timezone offset issues
    const parts = date.split('T')[0].split('-');
    if (parts.length !== 3) return '';
    const month = parts[1];
    const day = parts[2];
    const year = parts[0];
    return `${month}/${day}/${year}`;
  };

  const getStatusClass = (status?: string) => {
    switch (status) {
      case 'Cancel':
        return 'status-cancel';
      case 'Declined':
        return 'status-declined';
      case 'Paid':
        return 'status-paid';
      default:
        return '';
    }
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value: string, record: any) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
  });

  const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const columns: any[] = [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      ...getColumnSearchProps('firstName'),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      ...getColumnSearchProps('lastName'),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      ...getColumnSearchProps('phoneNumber'),
    },
    {
      title: 'Office',
      dataIndex: ['office', 'officeName'],
      key: 'officeName',
      ...getColumnSearchProps('officeName'),
    },
    {
      title: 'Agent Name',
      dataIndex: ['user', 'firstName'],
      key: 'agentName',
      render: (text: string, record: any) =>
        capitalizeFullName(`${record.user?.firstName} ${record.user?.lastName}`),
    },
    {
      title: 'State',
      dataIndex: ['address', 'state'],
      key: 'state',
      filters: Array.from(new Set(data.map((p) => p.address?.state).filter(Boolean))).map((state) => ({
        text: state,
        value: state,
      })),
      onFilter: (value: string, record: any) => record.address?.state === value,
    },
    {
      title: 'Carrier',
      dataIndex: ['majorMed', 'carrier'],
      key: 'carrier',
    },
    {
      title: 'Billing Date',
      dataIndex: ['majorMed', 'billingDate'],
      key: 'billingDate',
      render: (text: string) => dateFormat(text),
    },
    
    {
      title: 'Major Med Status',
      dataIndex: ['majorMed', 'status'],
      key: 'majorMedStatus',
      filters: [
        { text: 'Paid', value: 'Paid' },
        { text: 'Pending', value: 'Pending' },
        { text: 'Declined', value: 'Declined' },
        { text: 'Cancelled', value: 'Cancelled' },
        {text: 'Three-Way Call required', value: 'Three-Way Call required'},
        {text: 'SRE', value: 'SRE'},
        {text:'Contact Required', value:'contact required'}
      ],
      onFilter: (value: string, record: any) => record.majorMed?.status === value,
      render: (text: string) => <span className={getStatusClass(text)}>{text}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: any, record: any) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => onView?.(record._id)}
        >
          View
        </Button>
      ),
    },
  ];

  if (error) return <div>Error: {error.message}</div>;

  return (
    <Table
      columns={columns}
      dataSource={data as any[]}
      loading={loading}
      rowKey={(record) => record._id}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
        onShowSizeChange: (current, size) => setPageSize(size),
        onChange: (page) => setCurrentPage(page),
      }}
      scroll={{ x: 1200 }}
    />
  );
};

export default ACARequestsTable;
